<template>
  <div>
    <splide
      ref="splide"
      :options="splideOptions"
      :has-track="false"
      class="bracelet-finder__splide splide--pagination-reset"
    >
      <splide-track>
        <template
          v-for="(gaugeProduct, index) of gaugeData"
          :key="`slide-${index}`"
        >
          <splide-slide
            :data-product="gaugeProduct.id"
            :data-width="gaugeProduct.gauge"
            :data-slide="`slide-${index}`"
          >
            <responsive-image
              :image="gaugeProductImages[index] ? gaugeProductImages[index] : gaugeProduct.image"
              min-max="300-900"
              ratio="1:1"
              sizes="300px"
            />
          </splide-slide>
        </template>
      </splide-track>

      <div class="splide__arrows splide__arrows--ltr">
        <button
          type="button"
          class="splide__arrow splide__arrow--prev"
          :aria-label="$string('accessibility.carousel.previous')"
        >
          <icon-arrow-left />
        </button>

        <button
          type="button"
          class="splide__arrow splide__arrow--next"
          :aria-label="$string('accessibility.carousel.next')"
        >
          <icon-arrow-right />
        </button>
      </div>
    </splide>

    <div class="bracelet-finder__magnify-nav-container">
      <div class="bracelet-finder__magnify-nav" />
    </div>

    <div class="bracelet-finder__slider-nav">
      <splide
        ref="splideNav"
        :options="splideNavOptions"
        :has-track="false"
        class="bracelet-finder__splide-nav"
      >
        <splide-track>
          <template
            v-for="(gaugeType, index) of gaugeData"
            :key="gaugeType.gauge"
          >
            <splide-slide
              :data-metal="gaugeType.gauge"
            >
              <button
                :aria-label="`Select ${gaugeType.gauge}`"
                type="button"
                @click="handleSlideNavigation(index)"
              >
                {{ gaugeType.gauge }}
              </button>
            </splide-slide>
          </template>
        </splide-track>
      </splide>
    </div>

    <div class="bracelet-finder__actions bracelet-finder__actions-step-2">
      <btn
        :label="$string('bracelet_finder.confirm')"
        modifiers="primary"
        @click="handleConfirmation"
      />
    </div>

    <div class="bracelet-finder__gauge-images">
      <div
        v-for="(gaugeImage, index) of gaugeImages"
        :key="`image-${index}`"
        class="bracelet-finder__gauge-image-container"
      >
        <div
          class="bracelet-finder__gauge-image"
          @click.prevent="handleGaugeImageClick(index)"
        >
          <responsive-image
            :image="gaugeImage"
            min-max="50-350"
            ratio="1:1"
            :sizes="`(min-width: ${breakpoints.l}px) 350px, 50px`"
          />
        </div>

        <!-- Mobile image modal -->
        <teleport
          to="body"
        >
          <overlay
            :key="`image-${index}`"
            :show-overlay="false"
            :template="false"
            type="shelf"
            :namespace="`image-${index}`"
            block-class="bracelet-finder__gauge-image-modal"
            direction="up"
          >
            <template #body="overlay">
              <bracelet-finder-mobile-gallery
                :overlay="overlay"
                :current-image-index="index"
                :all-images="gaugeImages"
              />
            </template>
          </overlay>
        </teleport>
      </div>
    </div>
  </div>
</template>

<script>

/**
* Vue: Bracelet finder step 1 (bracelet-finder-step-1)
* -----------------------------------------------------------------------------
*
*
*/

import { mapActions, mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import metaobjectQuery from '~graphql/metaobject.gql'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { formatProduct } from '~/helpers/format'
import { convertToHandle } from '~/helpers/convert'
import { values as breakpoints } from '~/config/breakpoints'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'

import BraceletFinderMobileGallery from './bracelet-finder-mobile-gallery'

import Overlay from '~global/overlay/overlay'
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'BraceletFinderStepTwo',

  components: {
    Overlay,
    IconArrowLeft,
    IconArrowRight,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    Btn,
    BraceletFinderMobileGallery,
  },

  props: {
    metal: {
      type: String,
      default: '',
    },
    gaugeProductImages: {
      type: Array,
      default: () => [],
    },
    gaugeImages: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'confirm-step': (payload) => typeof payload === 'object',
    'previous-step': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      activeSlideIndex: 0,
      gaugeData: [],
      allProducts: [],
      splideOptions: {
        mediaQuery: 'min',
        flickMaxPages: 1,
        focus: 'center',
        gap: 'var(--spacing-m)',
        perMove: 1,
        perPage: 1.15,
        wheel: false,
        arrows: false,
        drag: true,
        flickPower: 200,
        pagination: false,
        type: 'slide',
        omitEnd: true,
        autoplay: false,
        respectReducedMotion: true,
        trimSpace: false,
        fixedWidth: '275px',
        fixedHeight: '275px',
        updateOnMove: true,
        breakpoints: {
          [breakpoints.m]: {
            gap: 'var(--spacing-xl)',
            perPage: 1,
          },
          [breakpoints.l]: {
            gap: '183px',
            keyboard: true,
            perMove: 1,
            perPage: 3,
            pagination: false,
            trimSpace: false,
            fixedWidth: '300px',
            fixedHeight: '300px',
            arrows: true,
          },
        },
      },
      splideNavOptions: {
        mediaQuery: 'max',
        focus: 'center',
        wheel: false,
        arrows: false,
        drag: false,
        flickPower: 200,
        perPage: this.gaugeImages.length,
        pagination: false,
        type: 'slide',
        omitEnd: true,
        autoplay: false,
        respectReducedMotion: true,
        keyboard: true,
        trimSpace: false,
        fixedWidth: '95px',
        breakpoints: {
          [breakpoints.m]: {
            perPage: 3,
            drag: true,
          },
        },
      },
      breakpoints,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.getBracelets(this.metal)
    this.$refs.splide.splide.on('moved', (newIndex) => {
      this.$refs.splideNav.go(newIndex)
      this.activeSlideIndex = newIndex
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    handleSlideNavigation(index) {
      this.$refs.splide.go(index)
      this.$refs.splideNav.go(index)
    },

    handleConfirmation() {
      const activeSlide = this.$refs.splide.splide.Components.Elements.slides[this.activeSlideIndex]
      const productId = activeSlide.dataset.product
      const chosenWidth = activeSlide.dataset.width

      const product = this.allProducts.find((product) => product.id.toString() === productId)
      const confirmationObject = {
        selection: product,
        step: 2,
      }

      this.setNewUrl(productId, chosenWidth)
      this.$emit('confirm-step', confirmationObject)
    },

    setNewUrl(productId, chosenWidth) {
      const urlParameters = new URLSearchParams(location.search)

      const currentWidth = chosenWidth ? chosenWidth.toLowerCase().replace('.', '-') : ''

      urlParameters.forEach((value, key) => urlParameters.delete(key))
      urlParameters.delete('step')
      if (productId) {
        urlParameters.set('step', '3')
        urlParameters.set('pid', productId)
        urlParameters.set('width', currentWidth)
        urlParameters.set('metal', this.metal)
      }

      const url = productId && urlParameters.toString()
        ? `${location.origin}${location.pathname}?${urlParameters.toString()}`
        : `${location.origin}${location.pathname}`

      history.pushState({
        html: '',
      }, '', url)
    },

    /**
     * Get whole bracelet finder metaobject using GraphQL query
     */
    async getBracelets(metal) {
      try {
        const response = await cnvs.Query({
          query: metaobjectQuery,
          variables: getVariables({
            handle: {
              type: 'bracelet_finder',
              handle: convertToHandle(metal),
            },
            metafieldIds: [
              ...getMetafieldIDs('product'),
              ...getMetafieldIDs('variant'),
            ],
          }),
        })

        const braceletMetaFields = response.metaobject.fields
        let gaugeProducts = braceletMetaFields.find((field) => field.key === 'gauge_product_list')
        gaugeProducts = gaugeProducts.references
        gaugeProducts = gaugeProducts.nodes.map(
          (product) => formatProduct({ product }),
        )
        gaugeProducts.forEach((gaugeProduct) => {
          const productImage = gaugeProduct.featuredImage
          const productId = gaugeProduct.id
          const productMeta = gaugeProduct.metafields

          const productGaugeMeta = productMeta.find((meta) => meta.key === 'gauge')
          const productGauge = productGaugeMeta.value.value
          const productGaugeInfo = {
            id: productId,
            image: productImage,
            gauge: productGauge,
          }

          this.gaugeData.push(productGaugeInfo)
        })
        this.allProducts = gaugeProducts
      } catch (error) {
        cnvs.ReportError('[Bracelet Finder]', error)
      }
    },

    handleGaugeImageClick(index) {
      if (this.isMobile) {
        const currentNameSpace = `image-${index}`
        this.openOverlay({
          namespace: currentNameSpace,
          ignoreDismissed: true,
          component: 'bracelet-finder-mobile-gallery',
        })
      }
    },
  },
}
</script>
